import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, PencilSimple, Trash, PlugsConnected, MagnifyingGlass } from "@phosphor-icons/react";
import SipCodecPicker, { DEFAULT_SIP_CODECS, normalizeSipCodecs } from "@/components/SipCodecPicker";

const empty = () => ({
  name: "", auth_mode: "ip_to_ip", host: "", port: 5060, transport: "UDP",
  username: "", password: "", permitted_source_ips: "", status: "active", notes: "",
  allowed_codecs: [...DEFAULT_SIP_CODECS],
});

export default function SipConnPage({
  title, subtitle, apiBase, testid, entityLabel, showCodecs = false,
  showAssignedInfra = false,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [infra, setInfra] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(apiBase, { params: q.trim() ? { q: q.trim() } : {} });
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, [apiBase, q]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!showAssignedInfra) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/customer-routing/assigned-infra");
        if (!cancelled) setInfra(data || { telephony: [], media: [] });
      } catch {
        if (!cancelled) setInfra({ telephony: [], media: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [showAssignedInfra]);

  const openNew = () => { setForm(empty()); setDlg("new"); };
  const openEdit = (r) => {
    setForm({
      name: r.name || "",
      auth_mode: r.auth_mode || "ip_to_ip",
      host: r.host || "",
      port: r.port || 5060,
      transport: r.transport || "UDP",
      username: r.username || "",
      password: "",
      permitted_source_ips: (r.permitted_source_ips || []).join(", "),
      status: r.status || "active",
      notes: r.notes || "",
      allowed_codecs: normalizeSipCodecs(r.allowed_codecs),
      _password_configured: r.password_configured,
      _password_hint: r.password_hint,
    });
    setDlg(r);
  };

  const save = async () => {
    if (!form.name.trim() || !form.host.trim()) {
      toast.error("Name and host are required"); return;
    }
    if (form.auth_mode === "sip_registration") {
      if (!form.username.trim()) { toast.error("Username is required for SIP registration"); return; }
      if (dlg === "new" && !form.password) { toast.error("Password is required for SIP registration"); return; }
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        auth_mode: form.auth_mode,
        host: form.host.trim(),
        port: Number(form.port) || 5060,
        transport: form.transport,
        username: form.auth_mode === "sip_registration" ? form.username.trim() : null,
        permitted_source_ips: form.permitted_source_ips
          .split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
        status: form.status,
        notes: form.notes || null,
      };
      if (showCodecs) {
        body.allowed_codecs = normalizeSipCodecs(form.allowed_codecs);
      }
      if (form.password) body.password = form.password;
      if (dlg === "new") {
        await api.post(apiBase, body);
        toast.success(`${entityLabel} created`);
      } else {
        await api.patch(`${apiBase}/${dlg.id}`, body);
        toast.success(`${entityLabel} updated`);
      }
      setDlg(null); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.name}?`)) return;
    try {
      await api.delete(`${apiBase}/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  const test = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await api.post(`${apiBase}/${r.id}/test`);
      toast[data.ok ? "success" : "error"](data.message || (data.ok ? "OK" : "Failed"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusy(null);
    }
  };

  const isReg = form.auth_mode === "sip_registration";

  const telephony = infra?.telephony || [];
  const media = infra?.media || [];

  return (
    <div className="space-y-6" data-testid={`${testid}-page`}>
      <PageHeader title={title} subtitle={subtitle} />

      {showAssignedInfra && (
        <div
          className="grid gap-3 sm:grid-cols-2"
          data-testid={`${testid}-assigned-infra`}
        >
          <div className="rounded-xl border border-border bg-card p-4 space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Telephony IP
            </p>
            <p className="text-sm text-muted-foreground">
              OpenSIPS signaling hosts assigned to your account (allowlist these toward vendors).
            </p>
            {infra == null ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : telephony.length === 0 ? (
              <p className="text-sm text-amber-700" data-testid={`${testid}-telephony-empty`}>
                None assigned — contact your administrator.
              </p>
            ) : (
              <ul className="space-y-2" data-testid={`${testid}-telephony-list`}>
                {telephony.map((s) => (
                  <li
                    key={s.id || s.host}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/80 bg-muted/30 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{s.name}</p>
                      <p className="font-mono text-sm tabular-nums">
                        {s.host}{s.port ? `:${s.port}` : ""}
                      </p>
                    </div>
                    <StatusPill status={s.status || "unknown"} />
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-xl border border-border bg-card p-4 space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Media IPs
            </p>
            <p className="text-sm text-muted-foreground">
              FreeSWITCH media hosts for outbound RTP / SIP from your campaigns.
            </p>
            {infra == null ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : media.length === 0 ? (
              <p className="text-sm text-amber-700" data-testid={`${testid}-media-empty`}>
                None assigned — contact your administrator.
              </p>
            ) : (
              <ul className="space-y-2" data-testid={`${testid}-media-list`}>
                {media.map((s) => (
                  <li
                    key={s.id || s.host}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/80 bg-muted/30 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{s.name}</p>
                      <p className="font-mono text-sm tabular-nums">
                        {s.host}{s.port ? `:${s.port}` : ""}
                      </p>
                    </div>
                    <StatusPill status={s.status || "unknown"} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="relative">
          <MagnifyingGlass className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input className="pl-8 w-56" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Button className="rounded-lg" onClick={openNew} data-testid={`${testid}-add`}>
          <Plus className="w-4 h-4 mr-1.5" /> Add {entityLabel}
        </Button>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p>
        : rows.length === 0 ? (
          <EmptyState title={`No ${entityLabel}s`} description={`Add an ${entityLabel} to configure routing.`} />
        ) : (
          <div className="rounded-xl border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Host</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Connection</TableHead>
                  <TableHead className="w-36" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-xs">
                      {r.auth_mode === "sip_registration" ? (
                        <span
                          className="inline-flex items-center gap-1.5"
                          title={
                            typeof r.registration_registered === "boolean"
                              ? (r.registration_registered
                                ? `Registered (${r.registration_state || "REGED"})`
                                : `Not registered (${r.registration_state || "UNKNOWN"})`)
                              : "SIP registration"
                          }
                        >
                          {typeof r.registration_registered === "boolean" && (
                            <span
                              className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                                r.registration_registered ? "bg-emerald-500" : "bg-red-500"
                              }`}
                              data-testid={`${testid}-reg-dot`}
                              aria-label={r.registration_registered ? "registered" : "not registered"}
                            />
                          )}
                          SIP registration
                        </span>
                      ) : (
                        "IP-to-IP"
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-mono">
                      {r.host}:{r.port} · {r.transport}
                    </TableCell>
                    <TableCell><StatusPill status={r.status} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.connection_status || "untested"}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" disabled={busy === r.id} onClick={() => test(r)} title="Test config">
                        <PlugsConnected className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)}><PencilSimple className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r)}><Trash className="w-4 h-4 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle>{dlg === "new" ? `Add ${entityLabel}` : `Edit ${entityLabel}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5"><Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`${testid}-name`} /></div>
            <div className="space-y-1.5"><Label>Connection mode</Label>
              <Select value={form.auth_mode} onValueChange={(v) => setForm({ ...form, auth_mode: v })}>
                <SelectTrigger data-testid={`${testid}-auth-mode`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ip_to_ip">IP-to-IP</SelectItem>
                  <SelectItem value="sip_registration">SIP registration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-1.5"><Label>SIP host / domain</Label>
                <Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} data-testid={`${testid}-host`} /></div>
              <div className="space-y-1.5"><Label>Port</Label>
                <Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Transport</Label>
                <Select value={form.transport} onValueChange={(v) => setForm({ ...form, transport: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UDP">UDP</SelectItem>
                    <SelectItem value="TCP">TCP</SelectItem>
                    <SelectItem value="TLS">TLS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {isReg && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Username</Label>
                  <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid={`${testid}-username`} /></div>
                <div className="space-y-1.5">
                  <Label>Password {form._password_configured ? <span className="text-muted-foreground font-normal">({form._password_hint} — leave blank to keep)</span> : null}</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" data-testid={`${testid}-password`} />
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Permitted source IPs <span className="text-muted-foreground font-normal">(comma-separated CIDRs)</span></Label>
              <Input value={form.permitted_source_ips} onChange={(e) => setForm({ ...form, permitted_source_ips: e.target.value })} placeholder="203.0.113.0/24, 198.51.100.10" />
            </div>
            {showCodecs && (
              <SipCodecPicker
                value={form.allowed_codecs}
                onChange={(allowed_codecs) => setForm({ ...form, allowed_codecs })}
                testid={`${testid}-codecs`}
                description="Codecs offered on outbound calls through this vendor. Select one or all."
              />
            )}
            <div className="space-y-1.5"><Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setDlg(null)}>Cancel</Button>
            <Button className="rounded-lg" onClick={save} disabled={saving} data-testid={`${testid}-save`}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
